import { chunk, uniq } from 'lodash'
import { useEffect, useState } from 'react'
import Spinner from 'app/components/Spinner'
import { createBatchLoader } from 'app/lib/batchLoader'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'

// Ids requested in the same tick go out as one `locations` list query instead of
// one `location(id)` request per table row. Keep the batch small enough for a
// GET request URL.
const BATCH_SIZE = 50

let fetching: Record<string, Promise<string>> = {}
let cache: Record<string, string> = {}

const nameLoader = createBatchLoader<string, string>(async (ids) => {
  const names = new Map<string, string>()
  await Promise.all(
    chunk(uniq(ids), BATCH_SIZE).map(async (batch) => {
      const { hits } = await gqlFetchList<{ id: string; name?: string }>(
        `locations`,
        { search: `id:(${batch.map((id) => `"${id}"`).join(` OR `)})`, pagination: { limit: batch.length } },
        `id name`,
      )
      for (const hit of hits ?? []) names.set(hit.id, hit.name || `Unknown`)
      for (const id of batch) if (!names.has(id)) names.set(id, `Unknown`)
    }),
  )
  return names
})

export async function getName(locationId: string) {
  if (cache[locationId] != null) return cache[locationId]
  if (fetching[locationId] != null) return fetching[locationId]
  if (!locationId || locationId === `undefined`) return
  return (fetching[locationId] = nameLoader.load(locationId).then((name) => (cache[locationId] = name)))
}

export function LazyLocationCell(props: {
  locationId: string
  className?: string
  loadingIndicator?: boolean
  /** Expose the resolved name as a native hover tooltip — for truncating contexts. */
  hoverTitle?: boolean
}) {
  const [name, setName] = useState<string>(cache[props.locationId])

  useEffect(() => {
    if (name && cache[props.locationId] === name) return
    else setName(``)
    bg(
      getName(props.locationId).then((name) => {
        if (name != null) setName(name)
      }),
    )
  }, [props.locationId])

  // Nothing to resolve. `getName` bails on a missing id, so without this the spinner
  // would spin forever instead of the cell simply rendering empty.
  if (!props.locationId || props.locationId === `undefined`) return null

  return name ? (
    <span title={props.hoverTitle ? name : undefined} className={props.className ?? 'text-body-xs'}>
      {name}
    </span>
  ) : props.loadingIndicator !== false ? (
    <Spinner size={5} />
  ) : null
}
